import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Button, Radio, Space, TimePicker, Typography } from 'antd';
import _ from 'lodash';
import moment from 'moment';

import { disabledMinutes, updateFilterItemInList } from '../../../../../../../../../helpers/utils/reports';
import { useActions } from '../../../../../../../hooks/useActions';
import { useForceUpdate } from '../../../../../../../hooks/useForceUpdate';
import { useTypedSelector } from '../../../../../../../hooks/useTypedSelector';

const TimeRangePicker = ({ disabled = false, id, index, modalVisibleHandler, prefix, ...props }: any) => {
    const finalFormat = 'HH:mm';

    const forceUpdate = useForceUpdate();

    const filters = useTypedSelector((state) => state.compare.compareFilters[`compareFilters${prefix}`]);
    const { setCompareFilter, setMetaFilterUpdate } = useActions();

    const [ isEditing, setIsEditing ] = useState(false);
    const [ filterMode, setFilterMode ] = useState(1);
    const [ _id, _setId ] = useState(id);

    // Приводим id к строке для безопасной работы с методами строк
    const safeId = String(id ?? '');

    useEffect(() => {
        forceUpdate();
    }, [ props.itemData, index, id ]);

    useEffect(() => {
        let _tempId = safeId;

        if (filterMode === 2) {
            setFilterMode(2);
            if (!safeId.includes('not_')) {
                _tempId = `not_${safeId}`;
            }
        } else {
            setFilterMode(1);
            if (safeId.includes('not_')) {
                _tempId = safeId.split('not_')[1];
            } else {
                _tempId = safeId;
            }
        }

        _setId(_tempId);
    }, [ safeId, filterMode ]);

    useEffect(() => {
        if (safeId.includes('not_')) {
            setFilterMode(2);
        } else {
            setFilterMode(1);
        }
    }, [safeId]);

    const filter = useMemo(() => {
        let _filter = props.itemData ? props.itemData : null;
        if (!props.list) {
            _filter = null;
        } else if (!isEditing) {
            setIsEditing(true);
        }

        return _filter ? _filter : { start: null, end: null, ...props };
    }, [ filters, _id, index, props.list, props.itemData, props ]);

    const [ currentData, setCurrentData ] = useState(filter);

    const onApply = () => {
        let _currentData = {
            ...currentData,
            id: _id,
            start: currentData.start ?
                moment(currentData.start, [ moment.ISO_8601, 'HH:mm' ]).format(finalFormat) :
                moment(moment('00:00', [ moment.ISO_8601, 'HH:mm' ]), [ moment.ISO_8601, 'HH:mm' ]).format(finalFormat),
            end: currentData.end ?
                moment(currentData.end, [ moment.ISO_8601, 'HH:mm' ]).format(finalFormat) :
                moment(moment('23:59', [ moment.ISO_8601, 'HH:mm' ]), [ moment.ISO_8601, 'HH:mm' ]).format(finalFormat)
        };

        if (isEditing) {
            let _filters = _.cloneDeep(filters);

            const payload = { ..._currentData };
            _filters = updateFilterItemInList(_filters, index, payload);

            setCompareFilter({ index: prefix, filters: _filters });
            setMetaFilterUpdate({ value:true });
            modalVisibleHandler(false);

            return;
        }

        const payload = {
            ..._currentData,
            id: _id
        };

        let _filters = _.cloneDeep(filters);
        _filters.push(payload);

        setCompareFilter({ index: prefix, filters: _filters });
        setMetaFilterUpdate({ value:true });
        modalVisibleHandler(false);
    };

    const [ startOpen, setStartOpen ] = useState(false);
    const [ endOpen, setEndOpen ] = useState(false);
    const lastStartMinuteRef = useRef<number | null>(null);
    const lastEndMinuteRef = useRef<number | null>(null);

    // --- Ограничения: ОТ всегда раньше ДО ---
    const baseDisabled = useMemo(() => disabledMinutes(), []);

    const startDisabledHours = useCallback(() => {
        if (!currentData.end) return [];
        const endM = moment(currentData.end, [ moment.ISO_8601, 'HH:mm' ]);
        if (!endM.isValid()) return [];
        return Array.from({ length: 24 }, (_, i) => i).filter(h => h > endM.hour());
    }, [currentData.end]);

    const startGetDisabledMinutes = useCallback((hour: number) => {
        if (!currentData.end) return baseDisabled;
        const endM = moment(currentData.end, [ moment.ISO_8601, 'HH:mm' ]);
        if (!endM.isValid()) return baseDisabled;
        if (hour < endM.hour()) return baseDisabled;
        if (hour === endM.hour()) {
            const range = Array.from({ length: 60 }, (_, i) => i).filter(m => m > endM.minute());
            return [ ...new Set([ ...baseDisabled, ...range ]) ];
        }
        return Array.from({ length: 60 }, (_, i) => i);
    }, [currentData.end, baseDisabled]);

    const endDisabledHours = useCallback(() => {
        if (!currentData.start) return [];
        const startM = moment(currentData.start, [ moment.ISO_8601, 'HH:mm' ]);
        if (!startM.isValid()) return [];
        return Array.from({ length: 24 }, (_, i) => i).filter(h => h < startM.hour());
    }, [currentData.start]);

    const endGetDisabledMinutes = useCallback((hour: number) => {
        if (!currentData.start) return baseDisabled;
        const startM = moment(currentData.start, [ moment.ISO_8601, 'HH:mm' ]);
        if (!startM.isValid()) return baseDisabled;
        if (hour > startM.hour()) return baseDisabled;
        if (hour === startM.hour()) {
            const range = Array.from({ length: 60 }, (_, i) => i).filter(m => m < startM.minute());
            return [ ...new Set([ ...baseDisabled, ...range ]) ];
        }
        return Array.from({ length: 60 }, (_, i) => i);
    }, [currentData.start, baseDisabled]);

    const TP = ({ value, type }: any) => {
        const _val = value ? moment(value, [ moment.ISO_8601, 'HH:mm' ]) : null;
        const isStart = type === 'start';
        const pickerOpen = isStart ? startOpen : endOpen;
        const setPickerOpen = isStart ? setStartOpen : setEndOpen;
        const lastMinuteRef = isStart ? lastStartMinuteRef : lastEndMinuteRef;

        return (
            <TimePicker
                disabled={disabled}
                placeholder={isStart ? 'с' : 'по'}
                format="HH:mm"
                disabledHours={isStart ? startDisabledHours : endDisabledHours}
                disabledMinutes={isStart ? startGetDisabledMinutes : endGetDisabledMinutes}
                hideDisabledOptions
                showNow={false}
                value={_val}
                open={pickerOpen}
                onOpenChange={(newOpen) => {
                    setPickerOpen(newOpen);
                    if (newOpen) {
                        lastMinuteRef.current = _val ? _val.minute() : null;
                    }
                }}
                onSelect={(val) => {
                    const _tempVal: moment.Moment | string = val || (isStart ? '00:00' : '23:59');
                    setCurrentData((prevState: any) => ({
                        ...prevState,
                        [type]: moment(_tempVal, [ moment.ISO_8601, 'HH:mm' ])
                    }));
                    const newMinute = moment(val).minute();
                    if (lastMinuteRef.current !== null && lastMinuteRef.current !== newMinute) {
                        setPickerOpen(false);
                    }
                    lastMinuteRef.current = newMinute;
                }}
            />
        );
    };

    return (
    <div>
        <Space size="small" direction="vertical">
            <div style={{ marginTop: 10 }}>
                <Radio.Group disabled={disabled} onChange={(e) => setFilterMode(e.target.value)} value={filterMode}>
                    <Radio value={1}>Включить</Radio>
                    <Radio value={2}>Исключить</Radio>
                </Radio.Group>
            </div>
            <div>
                <TP value={currentData.start} type={'start'} />
                <Typography.Text type="secondary">—</Typography.Text>
                <TP value={currentData.end} type={'end'} />
            </div>
        </Space>
        <br />
        <Button disabled={disabled} type="primary" style={{ marginTop: 20 }} onClick={onApply}>
            Применить
        </Button>
    </div>
    );
};

export default TimeRangePicker;
